/**
 * Zone-filter "You may also like" complementary recommendation widgets.
 *
 * Applies to every <product-recommendations> element on the page (cart drawer
 * + cart page share the product-complementary.liquid snippet, so both are
 * covered). Hides .horizontal-product cards whose data-product-plan-groups
 * doesn't include the shopper's zone (appstleGroupGid in
 * yf_delivery_selection), and collapses the whole widget if nothing remains,
 * so we don't render an empty "You may also like" block.
 *
 * Mirrors the strict rule from postcode-gate-collection.liquid:
 *   - No zone selected → no filtering (default site behavior)
 *   - Card with no plan groups → hidden
 *   - Card whose plan groups don't include the zone's group → hidden
 *
 * Re-runs when recommendations are injected async (MutationObserver), when
 * the cart refreshes (cart:refresh event, cartUpdate pubsub) and when the
 * suburb changes (yf:zone-changed, which also re-fetches the widgets).
 */
(function () {
  'use strict';
  if (window.__yfCompZoneFilterReady) return;
  window.__yfCompZoneFilterReady = true;

  // Inject once — keeps the feature's styles co-located with its behavior.
  (function injectStyles() {
    if (document.getElementById('yf-comp-sub-pricing-styles')) return;
    var css =
      '.yf-sub-note{display:flex;flex-wrap:wrap;align-items:baseline;gap:.5rem;font-size:.875rem;line-height:1.3}' +
      '.yf-sub-note[hidden]{display:none}' +
      '.yf-sub-note__sub{color:var(--color-sale,#c0392b);font-weight:600}' +
      '.yf-sub-note__base{opacity:.6;text-decoration:line-through}' +
      '.yf-sub-note__save{color:var(--color-sale,#c0392b);font-size:.75rem;font-weight:600;letter-spacing:.02em;white-space:nowrap}' +
      /* Cart-page line-price variant sits in a text-right cell. Force a single row so the trio
         matches the compact horizontal layout used on the complementary cards. */
      '.yf-line-price.yf-sub-note{justify-content:flex-end;flex-wrap:nowrap;white-space:nowrap;gap:.625rem}' +
      '.yf-line-price .yf-sub-note__sub,.yf-line-price .yf-sub-note__base{font-size:1rem}' +
      '.yf-line-price .yf-sub-note__save{font-size:.75rem}' +
      /* Drawer variant — narrow column, so stack the trio on three lines aligned hard right. */
      '.horizontal-product__total{min-width:96px;text-align:right}' +
      '.horizontal-product__total .yf-sub-note{flex-direction:column;align-items:flex-end;gap:.125rem;flex-wrap:nowrap}' +
      '.horizontal-product__total .yf-sub-note__sub{font-size:1rem}' +
      '.horizontal-product__total .yf-sub-note__base{font-size:.875rem}' +
      '.horizontal-product__total .yf-sub-note__save{font-size:.75rem}' +
      /* Drawer quantity column — fixed min-width so the quantity boxes line up
         vertically between rows, regardless of how wide the details column grows. */
      '.horizontal-product__quantity{min-width:84px}';
    var style = document.createElement('style');
    style.id = 'yf-comp-sub-pricing-styles';
    style.textContent = css;
    (document.head || document.documentElement).appendChild(style);
  })();

  var SELECTION_KEY = 'yf_delivery_selection';
  var CONTAINER_SELECTOR = 'product-recommendations, product-complementary';
  var CARD_SELECTOR = '.horizontal-product';
  var WRAPPER_SELECTOR = 'product-recommendations, .yf-cart-complementary';
  var attached = new WeakSet();

  // Cached winning plan id — the selling_plan on the first cart line (the
  // plan-unifier keeps all lines on the same plan, so any line works). null
  // = no subscription in cart, so no discount to display.
  var cachedWinnerPlan = null;

  function getSelection() {
    try {
      var raw = localStorage.getItem(SELECTION_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function currentZoneHandle() {
    var sel = getSelection();
    return sel && sel.zoneHandle ? String(sel.zoneHandle).toLowerCase().trim() : null;
  }

  function currentGroupSuffix() {
    var sel = getSelection();
    if (!sel || !sel.appstleGroupGid) return null;
    return String(sel.appstleGroupGid).replace(/.*\//, '').toLowerCase();
  }

  // Match a card against the shopper's zone via Appstle selling-plan-group
  // membership. data-product-plan-groups is rendered from
  // product.selling_plan_groups — the ground truth for which zones the
  // product can actually be subscribed to. A product with no plan groups
  // gets hidden (the recs are for subscription orders).
  function cardMatches(card, wantedGroupSuffix) {
    if (!wantedGroupSuffix) return true;
    var groupsAttr = (card.getAttribute('data-product-plan-groups') || '').toLowerCase();
    var planGroups = groupsAttr.split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    if (!planGroups.length) return false;
    for (var i = 0; i < planGroups.length; i++) {
      if (planGroups[i] === wantedGroupSuffix || planGroups[i].indexOf(wantedGroupSuffix) !== -1) {
        return true;
      }
    }
    return false;
  }

  function filterContainer(container) {
    var cards = container.querySelectorAll(CARD_SELECTOR);
    // No cards yet = recs haven't fetched. DO NOT hide — <product-recommendations>
    // uses IntersectionObserver to lazy-fetch and hidden elements never enter the
    // viewport, so pre-hiding kills the fetch entirely. Wait for cards to appear
    // (the MutationObserver below will re-invoke us when they do).
    if (cards.length === 0) return 0;

    var wantedGroup = currentGroupSuffix();
    // No zone chosen → show everything (matches site-wide default pre-gate).
    if (!wantedGroup) {
      cards.forEach(function (c) { c.style.display = ''; });
      var wrapAll = container.closest(WRAPPER_SELECTOR);
      if (wrapAll) wrapAll.style.display = '';
      return cards.length;
    }

    // Detect a Flickity-driven carousel inside this container. Flickity caches
    // cell positions when it initializes; if we hide cards with display:none
    // afterwards, the carousel keeps reserving slots for them and "scroll to
    // last" lands on a blank cell. For Flickity carousels we have to actually
    // detach the mismatched cards from the DOM, then reloadCells() so the
    // carousel regenerates its slide list. Outside a carousel, hiding via
    // display:none is fine.
    var flickityHost = container.matches('product-complementary.flickity')
      ? container
      : container.querySelector('product-complementary.flickity');
    var carousel = flickityHost && flickityHost.carousel;
    var slider = carousel && carousel.slider;

    var visible = 0;
    var toRemove = [];
    cards.forEach(function (card) {
      // Climb to the card's own horizontal-product root if we matched a descendant.
      var root = card.matches(CARD_SELECTOR) ? card : card.closest(CARD_SELECTOR);
      if (!root) return;
      if (cardMatches(root, wantedGroup)) {
        root.style.display = '';
        visible++;
      } else if (slider && flickityHost.contains(root)) {
        // Inside the Flickity carousel: queue for removal + reload.
        toRemove.push(root);
      } else {
        // Outside any carousel (e.g. popup): plain hide is enough.
        root.style.display = 'none';
      }
    });

    if (slider && toRemove.length) {
      toRemove.forEach(function (el) {
        if (el.parentNode) el.parentNode.removeChild(el);
      });
      try {
        slider.reloadCells();
        slider.resize();
        // Snap back to the first cell so the carousel doesn't end up scrolled
        // past whatever's left after the removal.
        if (typeof slider.select === 'function') slider.select(0, false, true);
      } catch (e) {}
    }

    // Collapse the whole widget (title, nav buttons, container) if nothing fits.
    var wrapper = container.closest(WRAPPER_SELECTOR);
    if (wrapper) wrapper.style.display = visible === 0 ? 'none' : '';
    return visible;
  }

  function filterAll() {
    document.querySelectorAll(CONTAINER_SELECTOR).forEach(filterContainer);
  }

  // ---------- Subscription-discount pricing ---------------------------------
  // If the cart already has a subscription plan active (or will — via the
  // unifier's cascade rule), show the discounted price on each rec so the
  // shopper sees the savings before they add. Mirrors the popup's applyPricing
  // but operates on the horizontal-product card shape and swaps the existing
  // .price element's contents.

  function winnerFromCart(cart) {
    if (!cart || !Array.isArray(cart.items)) return null;
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      if (it && it.selling_plan_allocation && it.selling_plan_allocation.selling_plan) {
        return String(it.selling_plan_allocation.selling_plan.id);
      }
      if (it && it.selling_plan) return String(it.selling_plan);
    }
    return null;
  }

  // Apply the subscription-discount trio (sub price + strikethrough base + save%)
  // by inserting a sibling .yf-sub-note next to the server-rendered .price.
  // We hide .price when sub is active, and hide .yf-sub-note when not.
  //
  // Why a sibling rather than rewriting .price innerHTML: other code (Appstle,
  // Shopify section re-renders, theme-level price updates) can overwrite the
  // .price contents asynchronously, which wiped out our layout. A sibling
  // element is a separate island those other systems don't touch.
  function applyPricing(container, planId) {
    var cards = container.querySelectorAll(CARD_SELECTOR + '[data-allocations]');
    cards.forEach(function (card) {
      var priceEl = card.querySelector('.price');
      if (!priceEl) return;

      var basePrice = parseInt(card.getAttribute('data-base-price'), 10);
      var baseFmt = card.getAttribute('data-base-price-fmt') || '';
      var allocs = [];
      try { allocs = JSON.parse(card.getAttribute('data-allocations') || '[]'); } catch (e) {}

      var match = null;
      if (planId) {
        for (var i = 0; i < allocs.length; i++) {
          if (String(allocs[i].id) === String(planId)) { match = allocs[i]; break; }
        }
      }

      var wantState = match && match.price < basePrice ? 'sub:' + match.id : 'base';
      // Idempotent: stored on the CARD (a JS property — not observed by the
      // MutationObserver — so repeat calls after our own DOM writes bail out).
      if (card.__yfSubState === wantState) return;

      // Lazily create the sibling note. Once per card — stays in DOM thereafter.
      var note = card.querySelector(':scope > .yf-sub-note, .horizontal-product__details .yf-sub-note');
      if (!note) {
        note = document.createElement('div');
        note.className = 'yf-sub-note';
        note.hidden = true;
        note.innerHTML =
          '<span class="yf-sub-note__sub"></span>' +
          '<span class="yf-sub-note__base"></span>' +
          '<span class="yf-sub-note__save"></span>';
        // Insert right after .price so it sits in the same visual slot.
        if (priceEl.parentNode) {
          priceEl.parentNode.insertBefore(note, priceEl.nextSibling);
        }
      }

      if (match && match.price < basePrice) {
        var savePct = Math.round((basePrice - match.price) / basePrice * 100);
        note.querySelector('.yf-sub-note__sub').textContent = match.fmt;
        note.querySelector('.yf-sub-note__base').textContent = baseFmt;
        note.querySelector('.yf-sub-note__save').textContent = 'Save ' + savePct + '%';
        note.hidden = false;
        priceEl.style.display = 'none';
      } else {
        note.hidden = true;
        priceEl.style.display = '';
      }
      card.__yfSubState = wantState;
    });
  }

  function priceAll() {
    document.querySelectorAll(CONTAINER_SELECTOR).forEach(function (el) {
      applyPricing(el, cachedWinnerPlan);
    });
  }

  function refreshWinnerFromCart() {
    return fetch('/cart.js', {
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin'
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        cachedWinnerPlan = winnerFromCart(cart);
        priceAll();
      })
      .catch(function () {});
  }

  // Attach a mutation observer to each recommendation container so we catch
  // async fetch completion. Idempotent via the WeakSet guard.
  function attachContainer(el) {
    if (attached.has(el)) return;
    attached.add(el);
    filterContainer(el);
    applyPricing(el, cachedWinnerPlan);
    try {
      new MutationObserver(function () {
        filterContainer(el);
        applyPricing(el, cachedWinnerPlan);
      }).observe(el, { childList: true, subtree: true });
    } catch (e) {}
  }

  function observeContainers() {
    document.querySelectorAll(CONTAINER_SELECTOR).forEach(attachContainer);
  }

  // The cart drawer and cart-page recs widgets are wholesale-replaced when
  // the cart refreshes (cart.js does innerHTML = <new section HTML>), so the
  // MutationObserver attached to the OLD <product-recommendations> stops
  // firing and the new element never gets one. A body-level observer catches
  // any new container the moment it enters the DOM, regardless of what event
  // triggered the swap or what its timing was relative to our fetch.
  function attachBodyObserver() {
    try {
      new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          var added = mutations[i].addedNodes;
          if (!added || !added.length) continue;
          for (var j = 0; j < added.length; j++) {
            var node = added[j];
            if (!node || node.nodeType !== 1) continue;
            if (node.matches && node.matches(CONTAINER_SELECTOR)) {
              attachContainer(node);
            }
            if (node.querySelectorAll) {
              var nested = node.querySelectorAll(CONTAINER_SELECTOR);
              for (var k = 0; k < nested.length; k++) attachContainer(nested[k]);
            }
          }
        }
      }).observe(document.body, { childList: true, subtree: true });
    } catch (e) {}
  }

  function run() {
    observeContainers();
    filterAll();
    priceAll();
  }

  function bootstrap() {
    attachBodyObserver();
    // Fetch cart once up-front so we have the winner before any recs inject.
    refreshWinnerFromCart().finally(run);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }

  // Cart refreshes replace the entire cart-items / drawer sections, so the
  // recs widget is a new element each time — re-attach observers, re-filter,
  // and re-fetch the winning plan (cart composition may have changed).
  document.addEventListener('cart:refresh', function () {
    setTimeout(function () { refreshWinnerFromCart().finally(run); }, 50);
  });

  // pubsub.cartUpdate carries the fresh cart payload — skip the extra fetch
  // and derive the winner directly from it.
  (function attachPubsub(retries) {
    if (!window.theme || !window.theme.pubsub || !theme.pubsub.PUB_SUB_EVENTS) {
      if ((retries || 0) < 50) setTimeout(function () { attachPubsub((retries || 0) + 1); }, 200);
      return;
    }
    try {
      theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, function (payload) {
        if (payload && payload.cart) {
          cachedWinnerPlan = winnerFromCart(payload.cart);
        }
        setTimeout(run, 50);
      });
    } catch (e) {}
  })(0);

  // If the shopper changes their zone in another tab the storage event fires;
  // re-filter so all widgets reflect the new zone without a reload.
  window.addEventListener('storage', function (e) {
    if (e.key === SELECTION_KEY) filterAll();
  });

  // Same-tab suburb change. Hide wrong-zone cards straight away, then
  // re-fetch each widget: cards dropped for the old zone were removed from
  // the DOM (Flickity), so only a fresh fetch brings back the ones that suit
  // the new zone. The container observer re-filters and re-prices them.
  document.addEventListener('yf:zone-changed', function () {
    filterAll();
    document.querySelectorAll('product-recommendations[data-url]').forEach(function (el) {
      if (typeof el.init === 'function') {
        try { el.init(); } catch (e) {}
      }
    });
  });
})();
